import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, IconButton, Tooltip, Typography, Chip, LinearProgress,
  Card, CardContent, useMediaQuery, useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  PlayArrow as StartIcon,
  Pause as HoldIcon,
  CheckCircle as CompleteIcon,
  TaskAlt as ApproveIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { getWorkOrderStatusConfig } from '../../utils/workOrderStatus';
import { ProTable } from '../../components/pro-table';

const ONBEHALF_PROVIDER_TC_CODE = 'CRP';

const STATUS_OPTIONS = [
  { value: 'PENDING_REVIEW', label: 'Pending Review' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIALLY_INVOICED', label: 'Partly invoiced' },
  { value: 'INVOICED', label: 'Invoiced' },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(amount || 0);

const formatDate = (date) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const WorkOrders = ({ currentUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedStatus = STATUS_OPTIONS.some((o) => o.value === searchParams.get('status')) ? searchParams.get('status') : '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [statistics, setStatistics] = useState(null);
  const [showOnBehalf, setShowOnBehalf] = useState(false);
  const [orgGroups, setOrgGroups] = useState([]);
  const statsRange = useRef(null);

  useEffect(() => {
    ApiService.getPortalCustomerGroups({ size: 100 })
      .then((res) => setOrgGroups(res?.data?.data || res?.data || []))
      .catch(() => setOrgGroups([]));

    if (currentUser?.organizationType === 'B2B' && currentUser?.typingCenterId) {
      ApiService.getTypingCenterById(currentUser.typingCenterId)
        .then((res) => {
          const tc = res?.data?.data || res?.data || {};
          setShowOnBehalf((tc.code || tc.organization_code) === ONBEHALF_PROVIDER_TC_CODE);
        })
        .catch(() => setShowOnBehalf(false));
    }
  }, [currentUser]);

  const fetchData = async ({ page, pageSize, search, status, groupId, startDate, endDate }) => {
    const rangeKey = `${(startDate || '').slice(0, 10)}|${(endDate || '').slice(0, 10)}`;
    if (statsRange.current !== rangeKey) {
      statsRange.current = rangeKey;
      ApiService.getWorkOrderStatistics(null, { startDate, endDate })
        .then((res) => setStatistics(res?.data?.data || res?.data || null))
        .catch(() => {});
    }
    const params = {
      limit: pageSize,
      offset: page * pageSize,
      ...(status && { status }),
      ...(search && { search }),
      ...(groupId && { groupId }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    const response = await ApiService.getWorkOrders(params);
    const data = response?.data?.data || response?.data || [];
    return { data: Array.isArray(data) ? data : [], total: response?.data?.total || data.length || 0 };
  };

  const handleStatusAction = async (workOrderId, action) => {
    try {
      const actions = {
        approve: 'approveWorkOrder',
        start: 'startWorkOrder',
        hold: 'holdWorkOrder',
        complete: 'completeWorkOrder',
      };
      await ApiService[actions[action]](workOrderId);
      window.location.reload();
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
    }
  };

  const getStatusChip = (status) => {
    const config = getWorkOrderStatusConfig(status);
    return <Chip label={config.label} color={config.color} variant={config.variant} size="small" />;
  };

  const columns = [
    {
      key: 'work_order_number',
      label: 'Work Order #',
      width: 140,
      render: (row) => (
        <Box>
          <Typography color="primary" sx={{ fontSize: '0.8125rem' }}>
            {row.workOrderNumber || row.work_order_number}
          </Typography>
          {(row.quotationNumber || row.quotation_number) && (
            <Typography variant="caption" color="text.secondary">
              From: {row.quotationNumber || row.quotation_number}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'organization_name',
      label: 'Organization',
      width: 200,
      render: (row) => (
        <Typography noWrap sx={{ fontSize: '0.8125rem', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.organizationName || row.organization_name}
        </Typography>
      ),
    },
    {
      key: 'order_date',
      label: 'Date',
      width: 110,
      render: (row) => formatDate(row.orderDate || row.order_date),
    },
    {
      key: 'progress',
      label: 'Progress',
      align: 'center',
      width: 100,
      render: (row) => {
        const completed = row.completedTasks || row.completed_tasks || 0;
        const total = row.totalTasks || row.total_tasks || 0;
        const percent = row.progressPercent || row.progress_percent || 0;
        return (
          <Box sx={{ width: 80 }}>
            <Typography sx={{ fontSize: '0.75rem', mb: 0.25 }}>{completed}/{total}</Typography>
            <LinearProgress variant="determinate" value={percent} sx={{ height: 4, borderRadius: 1 }} />
          </Box>
        );
      },
    },
    {
      key: 'grand_total',
      label: 'Total',
      align: 'right',
      width: 120,
      render: (row) => formatCurrency(row.grandTotal || row.grand_total),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      width: 120,
      render: (row) => getStatusChip(row.status),
    },
    {
      key: 'agent_name',
      label: 'Agent',
      width: 120,
      defaultVisible: false,
      render: (row) => row.agentName || row.agent_name || '-',
    },
    {
      key: 'services',
      label: 'Services',
      width: 160,
      defaultVisible: false,
      render: (row) => {
        const items = row.lineItems || row.line_items || [];
        const names = [...new Set(items.map((li) => li.serviceName || li.service_name).filter(Boolean))];
        if (names.length === 0) return '-';
        const rest = names.length - 1;
        return (
          <Tooltip title={names.join('\n')} arrow placement="left">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
              <Typography noWrap sx={{ fontSize: '0.8125rem', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {names[0]}
              </Typography>
              {rest > 0 && (
                <Chip label={`+${rest}`} size="small"
                  sx={{ height: 18, fontSize: '0.6875rem', bgcolor: 'action.hover', '& .MuiChip-label': { px: 0.75 } }} />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const statsCards = statistics ? [
    { label: 'Total', value: statistics.total || 0 },
    { label: 'Open', value: statistics.open_count || 0, status: 'OPEN' },
    { label: 'In Progress', value: statistics.in_progress_count || 0, status: 'IN_PROGRESS' },
    { label: 'Completed', value: statistics.completed_count || 0, status: 'COMPLETED' },
    { label: 'Partly invoiced', value: statistics.partially_invoiced_count || 0, status: 'PARTIALLY_INVOICED' },
    { label: 'Invoiced', value: statistics.invoiced_count || 0, status: 'INVOICED' },
    { label: 'Completed Value', value: formatCurrency(statistics.completed_value || 0), highlight: true },
  ] : null;

  const filterDefs = [
    { type: 'search', placeholder: isMobile ? 'Search...' : 'Search by work order number or organization...' },
    { type: 'status', key: 'status', label: 'Status', options: STATUS_OPTIONS, defaultValue: linkedStatus },
    ...(orgGroups.length > 0
      ? [{ type: 'select', key: 'groupId', label: 'Group', options: orgGroups.map((g) => ({ value: g.id, label: g.name })) }]
      : []),
    { type: 'dateRange' },
  ];

  const rowActionButtons = (row) => (
    <Box display="flex" justifyContent="flex-end" gap={0.5}>
      <Tooltip title="View">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/work-orders/${row.id}`); }}>
          <ViewIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.status === 'PENDING_REVIEW' && (
        <Tooltip title="Approve & Open">
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleStatusAction(row.id, 'approve'); }}>
            <ApproveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.status === 'OPEN' && (
        <Tooltip title="Start">
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleStatusAction(row.id, 'start'); }}>
            <StartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {row.status === 'IN_PROGRESS' && (
        <>
          <Tooltip title="Hold">
            <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); handleStatusAction(row.id, 'hold'); }}>
              <HoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Complete">
            <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleStatusAction(row.id, 'complete'); }}>
              <CompleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      )}
      {row.status === 'ON_HOLD' && (
        <Tooltip title="Resume">
          <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleStatusAction(row.id, 'start'); }}>
            <StartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const actionButtons = (
    <Box display="flex" gap={1}>
      {showOnBehalf && (
        <Button variant="contained" size="small" color="inherit"
          onClick={() => navigate('/work-orders/create-on-behalf')}
          sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
          Create on Behalf
        </Button>
      )}
      {can(currentUser, 'work_order', 'write') && (
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => navigate('/work-orders/create')}
          sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>
          Create Work Order
        </Button>
      )}
    </Box>
  );

  const mobileCard = (row) => (
    <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Typography color="primary" sx={{ fontSize: '0.9rem' }}>
              {row.workOrderNumber || row.work_order_number}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {row.organizationName || row.organization_name}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            {getStatusChip(row.status)}
            <ChevronRightIcon color="action" sx={{ fontSize: 20 }} />
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {formatDate(row.orderDate || row.order_date)}
          </Typography>
          <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>
            {formatCurrency(row.grandTotal || row.grand_total)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <ProTable
      tableKey="work-orders"
      columns={columns}
      fetchData={fetchData}
      filters={filterDefs}
      statsCards={statsCards}
      actions={actionButtons}
      onRowClick={(row) => navigate(`/work-orders/${row.id}`)}
      rowActions={rowActionButtons}
      countLabel="work orders"
      defaultPageSize={20}
      defaultDateRange={linkedStatus ? 'custom' : '30days'}
      rowRenderMobile={mobileCard}
    />
  );
};

export default WorkOrders;
